/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Emitter from '@exc/emitter';
import {fetch} from '@adobe/exc-app/network';
import {getOrgs, getToken, isSessionEnvValid} from './utils/IMSUtils';
import {GraphQlService} from '@exc/graphql';
import logout from './utils/logout';
import metrics, {Level} from '@adobe/exc-app/metrics';
import {storage} from '@exc/storage';
import uuid from 'uuid/v1';
import Visible from './utils/visibility';

const defaultListeners = [
  ['imslib.accesstoken', 'auth.accesstoken'],
  ['imslib.accesstokenexpired', 'auth.accesstokenexpired'],
  ['imslib.profile', 'auth.profile'],
  ['imslib.error', 'auth.error'],
  ['imslib.ready', 'auth.ready']
];

/**
 * IMS Validate Token scheduler default interval in millis
 */
const QA_INTERVAL = 120000;
const PROD_INTERVAL = 30000;
const BACKGROUND_INTERVAL = 1000 * 60 * 10;

let imsLoaded = false;

class Auth extends Emitter {
  load(configuration) {
    const {env, imsConfig, listeners} = configuration;
    this.env = env;
    this.imsEnv = imsConfig.environment || this.env;
    this.backupAccessTokensInfos = {};

    window.adobeid.storedEvents.forEach(lt => this.emit(lt));

    defaultListeners.forEach(lt => {
      document.addEventListener(lt[0], () => this.emit(lt[1]));
    });

    this.addListenersToAuth(listeners);
    this.logout = logout;
    this.visibility = new Visible();
    this.visibility.on('hidden', this.handleHidden);
    this.visibility.on('shown', this.handleShown);
    // TODO: Remove once verified nothing outside of Shell needs it
    this.logout.add(`https://exc-loginims.experience${this.imsEnv === 'prod' ? 'cloud' : '-qa.corp'}.adobe.com/exc-session/logout`);
    this.metrics = metrics.create('exc.auth.Auth');
    this.metrics.event('Auth.init');

    window.adobeid.onError = err => {
      // `err` is not always a string, so let's convert to an object for
      // consistent handling later on.
      if (typeof err === 'string') {
        err = {error: err};
      }
      this.metrics.error(`Error making a request in IMS ${imsLoaded ? 'after' : 'during'} init`, err);
      const {debug, error, name} = err;
      const status = ((debug || {}).url || {}).error;
      // The request was aborted and we don't really know why.
      if (error === 'aborted' && !imsLoaded) {
        this.emit('auth.requestAborted');
        return;
      }
      // There are a variety of cases that can trigger rate limit errors.
      if (error === 'rate_limited' || status === 'rate_limited' || name === 'rate_limit') {
        this.emit('auth.rateLimited');
      }
    };

    if (!imsLoaded) {
      return window.adobeid.loadPromise.then(() => {
        if (!imsLoaded) {
          imsLoaded = true;
          this.isSessionEnvValid(imsConfig.environment, this.imsEnv);
          this.setupValidate(this.imsEnv === 'prod' ? PROD_INTERVAL : QA_INTERVAL);
        }
        return this;
      });
    }
    return this;
  }

  isAuthenticated(force) {
    return window.adobeIMS.isSignedInUser() || force;
  }

  signIn(referrer) {
    let oauthState = uuid();
    storage.session.set('oauthState', oauthState);
    this.logout.remove();
    window.adobeIMS.signIn({locale: navigator.language, redirect_uri: referrer || '/', state: {oauthState}});
  }

  validateToken() {
    let {adobeIMS, adobeid} = window;
    const token = adobeIMS.getAccessToken();
    const clientId = adobeid.client_id;
    const params = {
      bearer_token: token,
      client_id: clientId,
      type: 'access_token'
    };
    const url = adobeIMS.IMSAPIs.validate_token;

    if (!token) {
      this.metrics.event('validate api call with null token', {level: Level.WARN});
    }
    return new Promise((res, rej) => {
      adobeIMS.makeApiCall(url, params, response => {
        !response.valid ? rej() : res();
      });
    });
  }

  /** Validate session to prevent CSRF attack. */
  async isValidSession() {
    let {adobeIMS} = window;
    const imsSessionState = adobeIMS.getState() && adobeIMS.getState().oauthState;
    const sessionStorageState = await storage.session.get('oauthState');
    if (!(imsSessionState || sessionStorageState) || imsSessionState !== sessionStorageState) {
      return false;
    }
    return true;
  }

  /** Gets session Id from decoded access token. */
  getSessionId() {
    const token = window.adobeIMS.getAccessToken();
    const {sid} = window.adobeIMS.decodeAccessToken(token) || {};
    return sid;
  }

  /**
   * A scheduler to poll on ims validate token.
   * @param {number} interval Polling interval in millis.
   */
  setupValidate(interval) {
    this.setIntervalId = setInterval(this.validateTokenAndEmit, interval);
    return this.setIntervalId;
  }

  /**
   * A helper to emit and auto clear token status and login status
   * Generates: auth.validatetokensuccess event - on successful validation.
   * Generates: auth.validatetokenfailed event - on invalid/expired token and clears login.
   * @returns {boolean} returns if the session is valid or not
   */
  validateTokenAndEmit = async () => {
    try {
      await this.validateToken();
      this.emit('auth.validatetokensuccess');
      return true;
    } catch (err) {
      this.emit('auth.validatetokenfailed');
      this.clear();
      return false;
    }
  }

  /**
   * On page shown this will:
   *   - if it's been in the background for > 30 seconds validate immediately
   *   - setup the validation loop
   */
  handleShown = async () => {
    if (!this.isAuthenticated()) {
      return;
    }
    clearInterval(this.setIntervalId);
    const timeSinceBackground = Date.now() - this.backgroundRefreshStart;
    if (timeSinceBackground > 30000) {
      const isValid = await this.validateTokenAndEmit();
      if (!isValid) {
        return;
      }
    }
    this.setupValidate(this.imsEnv === 'prod' ? PROD_INTERVAL : QA_INTERVAL);
  }

  /**
   * A helper to clear default validation loop, store time placed in background, and
   * add a new 10 minute wait loop to reduce IMS load.
   */
  handleHidden = () => {
    if (!this.isAuthenticated()) {
      return;
    }
    clearInterval(this.setIntervalId);
    this.backgroundRefreshStart = Date.now();
    this.setupValidate(BACKGROUND_INTERVAL);
  }

  async setCustomToken(token) {
    window.adobeid.standalone = {};
    await window.adobeIMS.setStandaloneToken(token);
    await pollForProfile();
  }

  async getLocalSession() {
    return await storage.local.get('unifiedShellSession') || {};
  }

  setLocalSession(session) {
    storage.local.set('unifiedShellSession', session);
  }

  /**
   * Get stored profile from IMS lib.
   * @param {boolean} signOutIfNone - Sign out if no profile is available (Default: true)
   * @returns {IMSProfile} profile
   */
  fetchUserProfile(signOutIfNone = true) {
    const profile = window.adobeIMS.getUserProfile();
    if (profile) {
      return profile;
    }
    // There is a session, so likely the profile is failing to load for some
    // reason related to 429 or other error from the IMS request. There will
    // likely be an error shown to the user, so we don't need to log out.
    if (window.adobeIMS.getAccessToken() || !signOutIfNone) {
      return null;
    }
    this.signOut(true);
  }

  // add caching here separately if required
  async fetchProfileAndAccessTokenInfo(clientId, scope) {
    if (!clientId || !scope) {
      return null;
    }
    return await getToken(clientId, scope, this.imsEnv);
  }

  fetchAccessToken() {
    const accessTokenInfo = this.fetchAccessTokenInfo();
    return accessTokenInfo && accessTokenInfo.access_token;
  }

  fetchAccessTokenInfo() {
    return window.adobeIMS.getAccessTokenInfo();
  }

  async signOut(authExpired) {
    if (this.isAuthenticated()) {
      await this.logout.invoke();
    }
    this.clear();
    const {origin, search} = window.location;

    // Restore the backed up access tokens info in the event that it was popped
    // and the log out redirect never happened.
    if (!Object.keys(window.adobeIMS._accessTokensInfos).length && this.backupAccessTokensInfos && Object.keys(this.backupAccessTokensInfos).length) {
      window.adobeIMS._accessTokensInfos = this.backupAccessTokensInfos;
    }
    // Back up the access token info just in case the user doesn't complete the
    // log out action (could happen if solution prevents default in the
    // `beforeunload` event).
    this.backupAccessTokensInfos = window.adobeIMS._accessTokensInfos;
    window.adobeIMS.signOut((search || authExpired) ? {} : {redirect_uri: `${origin}/login`});
  }

  clear() {
    storage.local.clearOnLogout();
    storage.session.clear();
    this.logout.remove();
    this.setIntervalId && clearInterval(this.setIntervalId);
  }

  /**
   * Get org id to org name map.
   * @returns {Object} Org name map.
   */
  fetchOrgs() {
    this.metrics.event('failed to fetch org data from gql', {level: Level.ERROR});
    return getOrgs(this.getClientId(), this.imsEnv);
  }

  /**
   * Checks if all org names for profile are available.
   * @param {array} orgsMap - Array of org names
   * @returns {boolean} True if all profile orgs are in map, false otherwise.
   */
  allOrgsInProfile(orgsMap) {
    try {
      const profile = this.fetchUserProfile(false);
      if (orgsMap && orgsMap.length && profile) {
        const pcEntries = profile.projectedProductContext.map(pc => pc.prodCtx.owningEntity);
        const orgs = orgsMap.map(org => `${org.orgRef.ident}@${org.orgRef.authSrc}`);
        if (pcEntries.every(org => orgs.includes(org))) {
          return true;
        }
        this.metrics.event('Auth.cachedOrgsStale');
        return false;
      }
      profile && this.metrics.event('Auth.noCachedOrgs');
    } catch (err) {
      this.metrics.event('Auth.cachedOrgsError', {err: err.toString()});
    }
    return false;
  }

  /**
   * Async method to handle org switching.
   * Each time an org switch occur, we need to call the Login Service to
   * authorize the new org and update IMS session data
   * and Renga last loggedIn tenant.
   * @param {string} newOrgId To be updated IMS Org Id.
   * @param {string} currentOrgId Current Org Id.
   * @returns {Object} Optimistically returns org id for update.
   */
  async changeOrg(newOrgId, currentOrgId) {
    const profile = this.fetchUserProfile();
    const accessToken = this.fetchAccessToken();
    const imsUserProfile = {
      access_token: accessToken,
      projectedProductContext: profile.projectedProductContext,
      session: profile.session,
      userId: profile.userId
    };
    const orgUpdateReqBody = {
      currentImsOrgId: currentOrgId,
      imsProfile: JSON.stringify(imsUserProfile),
      requestedImsOrgId: newOrgId
    };
    // TODO: Remove once verified nothing outside of Shell needs it
    const url = `https://exc-loginims.experience${this.imsEnv === 'prod' ? 'cloud' : '-qa.corp'}.adobe.com/exc-session/imsorgupdate`;
    fetch(url, {
      body: JSON.stringify(orgUpdateReqBody),
      headers: {Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json'},
      method: 'POST'
    });

    let localSession = await this.getLocalSession();
    localSession.activeOrg = newOrgId;
    this.setLocalSession(localSession);

    this.metrics.event('Auth.changeOrg.done', {currentOrgId, newOrgId});

    // Get localStorage activeOrg, return custom response
    return {activeOrgId: `${localSession.activeOrg}`};
  }

  addListenersToAuth(listeners) {
    listeners &&
      Object.keys(listeners).forEach(event => {
        this.on(event, listeners[event]);
      });
  }

  getClientId() {
    return window.adobeid.client_id;
  }

  authorize(redirect, userId) {
    const clientId = this.getClientId();
    const url = new URL(window.adobeIMS.IMSAPIs.authorize.endpoint[clientId]);

    url.searchParams.set('client_id', clientId);
    url.searchParams.set('response_type', 'token');
    url.searchParams.set('scope', window.adobeid.scope);
    redirect && url.searchParams.set('redirect_uri', redirect);

    let oauthState = uuid();
    storage.session.set('oauthState', oauthState);
    url.searchParams.set('state', JSON.stringify({oauthState}));

    if (userId) {
      url.searchParams.set('prompt', 'none');
      url.searchParams.set('target_profile', userId);
    }
    window.location.assign(url.toString());
  }

  isSessionEnvValid(imsEnv, buildEnv) {
    if (!isSessionEnvValid(imsEnv, buildEnv)) {
      this.metrics.event('Auth.ims.conflict', {level: Level.ERROR});
      this.signOut(true);
    }
  }

  async signoutOnError(error, description) {
    const errors = (error || {}).errors || {};
    if (401 in errors) {
      await this.signOut();
      return true;
    }
    if (429 in errors) {
      this.emit('auth.rateLimited');
      return true;
    }
    this.metrics.error(`${description} Load error: ${Object.keys(errors).join(', ')}`, error);
    return false;
  }

  /**
   * Gets additional user data from GraphQL (Account Cluster & Behance)
   * @param imageKeySize
   * @returns {Promise<*>} Additional User Data
   */
  async getAdditionalUserData(imageKeySize) {
    const gqlService = new GraphQlService();
    try {
      return await gqlService.getAdditionalUserData({imageKeySize});
    } catch (error) {
      await this.signoutOnError(error, 'Account Cluster');
    }
  }

  async gqlOrgLaunchDarkly() {
    const gqlService = new GraphQlService();
    try {
      return await gqlService.launchDarklyFeatureFlags();
    } catch (error) {
      this.metrics.error('GQL failed to fetch Launch Darkly feature flags');
    }
  }

  /**
   * Gets user sandboxes for the current IMS org from GraphQL.
   * @param {string} selectedImsOrg Current IMS Org
   * @returns {Promise<Sandbox[]>} Sandboxes array
   */
  async gqlUserSandboxes(selectedImsOrg) {
    let variables;
    // Based on where AEP (serviceCode acp) was provisioned, we need to get the user sandboxes from
    // platform-stage when Shell is running on QA/Dev which are normally linked to platform-int.
    // The fulfillable data field provides the correct environment to the gql service.
    if (this.env === 'dev' || this.env === 'qa') {
      const {projectedProductContext} = this.fetchUserProfile();
      const {prodCtx} = projectedProductContext.find(({prodCtx}) =>
        prodCtx.serviceCode === 'acp' &&
        prodCtx.owningEntity === selectedImsOrg &&
        prodCtx.fulfillable_data
      ) || {};
      variables = prodCtx && {fulfillableData: JSON.parse(prodCtx.fulfillable_data)};
    }
    const gqlService = new GraphQlService();
    try {
      // TO DO: EXC-15057: Can we actually use the selectedImsOrg above in this GQL call so there is never a
      // mismatch in the org that is sent with the GQL request.
      return await gqlService.getUserSandboxes(variables);
    } catch (error) {
      await this.signoutOnError(error, 'User Sandboxes');
    }
  }

  /**
   * Get a new access token from IMS
   */
  refreshToken() {
    return new Promise(window.adobeIMS.acquireAccessToken);
  }

  /**
   * Gets Header data from GraphQL (Pulse)
   * @param {string} locale User Locale
   * @returns {Promise<*>} Header data
   */
  async gqlHandlerForHeader(locale, getProfile) {
    const gqlService = new GraphQlService();
    try {
      return await gqlService.initializeShellDataForHeader({
        config_version: 0,
        keys: ['lastClickTimestamp', 'lastSeenAnnouncementsTimestamp', 'lastNotificationSeenTimestamp'],
        locale
      }, getProfile);
    } catch (error) {
      await this.signoutOnError(error, 'GQL Header');
    }
  }

  /**
   * Get initial data for Shell - User Profile, Org & Login data from GraphQL
   * @param {boolean} isReload - True for reload, false for Login
   * @param {string} pathTenantId - Tenant ID (If part of the location hash)
   * @returns {Promise<*>} Initial data
   */
  async gqlHandler(isReload, pathTenantId) {
    // Grab the org from the path and use that as the selected org. If there
    // is no org yet, use the selected one from IMS.
    const gqlService = new GraphQlService();
    let shellInitData;
    try {
      if (isReload) {
        shellInitData = await gqlService.initializeShellDataForReload();
      } else {
        // Login
        shellInitData = await gqlService.initializeShellDataForLogin({
          pathTenantId
        });
        await this.updateProfile((shellInitData || {}).userProfileJson);
      }

      return shellInitData;
    } catch (error) {
      if (!await this.signoutOnError(error, 'GQL') && !isReload) {
        await this.updateProfile((shellInitData || {}).userProfileJson);
      }
    }
  }

  updateProfile(profile) {
    if (!profile) {
      return new Promise(resolve => {
        // Only relevant to login flow - get profile with existing token with fetchProfile
        window.adobeIMS.fetchProfile(window.adobeIMS, this.fetchAccessToken(), () => resolve(window.adobeIMS.getUserProfile()));
      });
    } else {
      window.adobeIMS.setProfile(profile);
      // set fetched profile from gql in IMS Session storage
      return Promise.resolve(profile);
    }
  }
}
/**
 * Method to check and wait for profile every 100 ms before continuing.
 * @returns {Promise.<*>} Resolve when a profile exists.
 */
function pollForProfile() {
  return new Promise(res => {
    function check() {
      if (window.adobeIMS.getUserProfile()) {
        return res();
      }
      setTimeout(() => check(), 100);
    }
    check();
  });
}

let instance = new Auth();
let loading = null;

export const AuthInstance = async config => {
  if (!instance.ready) {
    if (!loading) {
      loading = instance.load(config);
    }

    return loading
      .catch(err => {
        throw err;
      })
      .then(() => {
        instance.ready = true;
        return instance;
      });
  }
  return instance;
};
